import dayjs from 'dayjs';

import { DATE_MONTH_DAY, SECONDS_IN_HOUR, TIME_AMPM } from '@cast/constants';
import {
  ChartTooltipElementProps,
  DataPopperRow,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { getCurrencyPrecision, toNumOrZero } from '@cast/utils';

import { Money } from 'components/money';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { ChartType } from 'types/charts';
import { computeSpendColorsMap, ComputeSpendType } from 'types/cost-report';
import { granularityToSeconds } from 'utils/costReport';

import { useCostOverTime } from '../../../../cost-report/hooks/useCostOverTime';
import { ComputeSpendChartData } from '../../../../cost-report/types/costOverTime';

type TooltipRowProps = {
  label: string;
  cost: number | null;
  projected: number | null;
  type: ComputeSpendType;
  hasProjectedCost: boolean;
};

const TooltipRow = ({
  label,
  cost,
  projected,
  type,
  hasProjectedCost,
}: TooltipRowProps) => {
  const costPrecision = cost !== null ? getCurrencyPrecision(cost) : 0;
  const projectedPrecision =
    projected !== null ? getCurrencyPrecision(projected) : 0;

  return (
    <DataPopperRow
      indicator={{
        color: computeSpendColorsMap[type]!,
      }}
      left={<Typography variant="L10R">{label}</Typography>}
      right={
        <Stack direction="row" flexWrap="nowrap" gap={16}>
          <Typography variant="P10R" align="right" minWidth="50px">
            {cost !== null ? (
              <Money
                amount={cost}
                precision={costPrecision}
                fixed={costPrecision}
              />
            ) : (
              '--'
            )}
          </Typography>

          {hasProjectedCost && (
            <Typography
              variant="P10R"
              align="right"
              minWidth="50px"
              sx={{ opacity: 0.5 }}
            >
              {projected !== null ? (
                <Money
                  amount={projected}
                  precision={projectedPrecision}
                  fixed={projectedPrecision}
                />
              ) : (
                '--'
              )}
            </Typography>
          )}
        </Stack>
      }
    />
  );
};

type TooltipData = ComputeSpendChartData & { timestamp: string };

type UseComputeSpendTooltipArgs = {
  showStorage: boolean;
  timezone: string;
  isAnywhereProvider: boolean;
};

const getTotals = ({
  data,
  showStorage,
  projectEndOfDayCost,
}: {
  data: TooltipData;
  showStorage: boolean;
  projectEndOfDayCost: boolean;
}) => {
  const totalCost =
    toNumOrZero(data.onDemand) +
    toNumOrZero(data.spot) +
    toNumOrZero(data.fallback) +
    (showStorage ? toNumOrZero(data.storage) : 0);

  const totalProjectedCost = projectEndOfDayCost
    ? toNumOrZero(data.onDemandForecast) +
      toNumOrZero(data.spotForecast) +
      toNumOrZero(data.fallbackForecast) +
      (showStorage ? toNumOrZero(data.storageForecast) : 0)
    : 0;

  return { totalCost, totalProjectedCost };
};

export const useComputeSpendTooltip = ({
  showStorage,
  timezone,
  isAnywhereProvider,
}: UseComputeSpendTooltipArgs) => {
  const { chartType, granularity } = useCostReportContext();
  const { projectEndOfDayCost } = useCostOverTime();

  const isForecastForFutureDay = (data: TooltipData) => {
    if (!data.forecast) {
      return false;
    }
    // For bar charts (daily granularity), today's bar is "projected", not "forecast"
    if (chartType === ChartType.BAR) {
      return !dayjs
        .utc(data.timestamp)
        .tz(timezone)
        .isSame(dayjs().tz(timezone), 'day');
    }
    return true;
  };

  const tooltipHeader = ({
    data,
    testId,
  }: ChartTooltipElementProps<TooltipData>) => {
    const date = dayjs.utc(data.timestamp).tz(timezone);
    const { totalCost, totalProjectedCost } = getTotals({
      data,
      showStorage,
      projectEndOfDayCost,
    });
    const hasProjectedCost = totalCost < totalProjectedCost;

    const showTime =
      chartType === ChartType.AREA &&
      granularityToSeconds(granularity) <= SECONDS_IN_HOUR &&
      !hasProjectedCost;

    return (
      <DataPopperRow
        testId={testId}
        left={
          <Typography
            variant="L10M"
            color={tokens.theme.fg.structural.secondary}
          >
            {date.format(DATE_MONTH_DAY)}
          </Typography>
        }
        right={
          isForecastForFutureDay(data) ? (
            <Typography
              variant="L10M"
              color={tokens.theme.fg.structural.secondary}
            >
              Forecast
            </Typography>
          ) : (
            <Stack direction="row" gap={8}>
              {showTime ? (
                <Typography
                  variant="L10M"
                  color={tokens.theme.fg.structural.secondary}
                >
                  {date.format(TIME_AMPM)}
                </Typography>
              ) : null}
              {hasProjectedCost ? (
                <Typography
                  variant="L10M"
                  color={tokens.theme.fg.structural.secondary}
                >
                  Projected
                </Typography>
              ) : null}
            </Stack>
          )
        }
      />
    );
  };

  const tooltipFooter = ({ data }: ChartTooltipElementProps<TooltipData>) => {
    const { totalCost, totalProjectedCost } = getTotals({
      data,
      showStorage,
      projectEndOfDayCost,
    });
    const hasProjectedCost = totalCost < totalProjectedCost;
    const totalCostPrecision = getCurrencyPrecision(totalCost);
    const totalProjectedPrecision = getCurrencyPrecision(totalProjectedCost);
    return (
      <>
        <DataPopperRow
          dividerBottom
          left={<Typography variant="L10M">Compute cost</Typography>}
          right={
            <Stack direction="row" flexWrap="nowrap" gap={16}>
              <Typography variant="P10B" align="right" minWidth="50px">
                <Money
                  amount={totalCost}
                  precision={totalCostPrecision}
                  fixed={totalCostPrecision}
                />
              </Typography>
              {hasProjectedCost ? (
                <Typography
                  variant="P10B"
                  align="right"
                  minWidth="50px"
                  sx={{ opacity: 0.5 }}
                >
                  <Money
                    amount={totalProjectedCost}
                    precision={totalProjectedPrecision}
                    fixed={totalProjectedPrecision}
                  />
                </Typography>
              ) : null}
            </Stack>
          }
        />
        {showStorage ? (
          <TooltipRow
            label="Storage"
            cost={data.storage ?? null}
            projected={data.storageForecast ?? null}
            type={ComputeSpendType.STORAGE}
            hasProjectedCost={hasProjectedCost}
          />
        ) : null}
        {!isAnywhereProvider ? (
          <>
            <TooltipRow
              label="On-Demand"
              cost={data.onDemand ?? null}
              projected={data.onDemandForecast ?? null}
              type={ComputeSpendType.ON_DEMAND}
              hasProjectedCost={hasProjectedCost}
            />
            {!isForecastForFutureDay(data) ? (
              <TooltipRow
                label="Fallback"
                cost={data.fallback ?? null}
                projected={data.fallbackForecast ?? null}
                type={ComputeSpendType.FALLBACK}
                hasProjectedCost={hasProjectedCost}
              />
            ) : null}
            <TooltipRow
              label="Spot"
              cost={data.spot ?? null}
              projected={data.spotForecast ?? null}
              type={ComputeSpendType.SPOT}
              hasProjectedCost={hasProjectedCost}
            />
          </>
        ) : null}
      </>
    );
  };

  return { tooltipHeader, tooltipFooter };
};
